import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, Satellite, Clock, Waves, Loader2, Compass, AlertTriangle, Gauge, Info } from "lucide-react";
import { riskOf, countdown, ISLAND_PHOTO } from "@/lib/geo";
import { briefingUrl, sentinelUrl } from "@/lib/api";

const confColor = (c) => (c >= 70 ? "#00ff87" : c >= 45 ? "#FF9F1C" : "#94a3b8");

export default function BriefingPanel({ island, onOpenCoastDetail }) {
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(false);
  const [sat, setSat] = useState(null);
  const [satLoading, setSatLoading] = useState(false);
  const [satError, setSatError] = useState(false);
  const esRef = useRef(null);

  const startBriefing = (id) => {
    if (esRef.current) esRef.current.close();
    setText("");
    setError(false);
    setStreaming(true);
    const es = new EventSource(briefingUrl(id));
    esRef.current = es;
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.delta) setText((t) => t + d.delta);
        if (d.error) setError(true);
        if (d.done) {
          setStreaming(false);
          es.close();
        }
      } catch (_) {}
    };
    es.onerror = () => {
      setStreaming(false);
      es.close();
    };
  };

  useEffect(() => {
    if (island) {
      setSat(null);
      setSatError(false);
      startBriefing(island.id);
    }
    return () => esRef.current?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [island?.id]);

  const loadSentinel = () => {
    if (!island) return;
    setSatLoading(true);
    setSatError(false);
    setSat(null);
    const url = sentinelUrl({
      west: island.lng - 0.6,
      south: island.lat - 0.6,
      east: island.lng + 0.6,
      north: island.lat + 0.6,
      width: 640,
      height: 640,
      days: 75,
    });
    // cache-bust so a retry always re-requests
    setSat(`${url}&t=${Date.now()}`);
  };

  if (!island) {
    return (
      <div className="glass flex h-full flex-col items-center justify-center rounded-xl p-6 text-center">
        <Sparkles className="mb-3 h-6 w-6 text-cyan-400/60" />
        <p className="text-sm text-slate-400">
          Select an island from the risk ticker or map to generate an AI advisory briefing.
        </p>
      </div>
    );
  }

  const rc = riskOf(island.risk);
  const cd = island.eta_utc ? countdown(island.eta_utc) : { text: "> 72h", over: false };

  return (
    <div className="thin-scroll flex h-full flex-col gap-3 overflow-y-auto pr-1" data-testid="briefing-panel">
      {/* island header */}
      <div className="glass relative overflow-hidden rounded-xl">
        <div
          className="h-28 w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${ISLAND_PHOTO})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07132b] via-[#07132b]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-3">
          <div>
            <div className="eyebrow text-cyan-400/80">Advisory target</div>
            <div className="font-display text-xl font-extrabold text-white">{island.name}</div>
          </div>
          <span className={`rounded-md border px-2 py-1 font-mono-tel text-[11px] font-bold ${rc.chip}`}>
            {rc.label}
          </span>
        </div>
      </div>

      {/* quick stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="glass rounded-lg p-2.5">
          <div className="flex items-center gap-1 eyebrow text-slate-500">
            <Clock className="h-3 w-3" /> ETA
          </div>
          <div className={`font-mono-tel text-base font-bold ${rc.text}`}>{cd.text}</div>
        </div>
        <div className="glass rounded-lg p-2.5">
          <div className="flex items-center gap-1 eyebrow text-slate-500">
            <Waves className="h-3 w-3" /> Biomass
          </div>
          <div className="font-mono-tel text-base font-bold text-biomass">{island.biomass_tkm2}</div>
        </div>
        <div className="glass rounded-lg p-2.5">
          <div className="flex items-center gap-1 eyebrow text-slate-500">
            <Compass className="h-3 w-3" /> Coast
          </div>
          <div className="font-mono-tel text-base font-bold text-amberx" data-testid="impact-coast">
            {island.impact_coast}
          </div>
        </div>
      </div>

      {/* ensemble landfall probability */}
      <div className="glass rounded-lg p-3" data-testid="landfall-prob">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="eyebrow text-slate-400">Ensemble landfall probability</span>
          {island.eta_lo != null && (
            <span className="font-mono-tel text-[11px] text-slate-500">
              arrival +{Math.round(island.eta_lo)}–{Math.round(island.eta_hi)}h
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["24h", island.p_landfall_24], ["48h", island.p_landfall_48], ["72h", island.p_landfall_72]].map(
            ([lbl, p]) => (
              <div key={lbl} className="rounded-md bg-white/[0.03] py-1.5">
                <div className="font-mono-tel text-lg font-bold" style={{ color: rc.hex }}>
                  {p != null ? `${Math.round(p * 100)}%` : "—"}
                </div>
                <div className="eyebrow text-slate-500">≤ {lbl}</div>
              </div>
            ),
          )}
        </div>
      </div>

      {/* affected-coast callout */}
      <div className="glass alert-glow flex items-center gap-2 rounded-lg border-amber-500/40 px-3 py-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amberx" />
        <p className="text-[12px] leading-snug text-amber-100/90">
          {island.impact_coast === "Offshore" ? (
            <>Mats remain <span className="font-semibold">offshore</span> — no coastline exposed yet.</>
          ) : (
            <>
              Mats approaching from the <span className="font-semibold">{island.impact_coast}</span> — the{" "}
              <span className="font-semibold text-amberx">{island.impact_coast} coast</span> is most exposed.
            </>
          )}
        </p>
      </div>

      <button
        onClick={() => onOpenCoastDetail?.(island.id)}
        data-testid="open-coast-detail-btn"
        className="glass flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors hover:border-cyan-400/40 hover:bg-cyan-500/5"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-100">
          <Compass className="h-4 w-4 text-cyanx" /> View Coast Detail
        </span>
        <span className="eyebrow text-slate-500">beaches · zoom · scan</span>
      </button>

      {/* forecast confidence + caveats */}
      <div className="glass rounded-lg p-3" data-testid="confidence-block">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 eyebrow text-slate-400">
            <Gauge className="h-3.5 w-3.5 text-cyanx" /> Forecast confidence
          </span>
          <span className="font-mono-tel text-sm font-bold" style={{ color: confColor(island.confidence) }}>
            {island.confidence ?? "—"}%
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div className="h-full rounded-full"
            style={{ width: `${island.confidence ?? 0}%`, background: confColor(island.confidence) }} />
        </div>
        {island.caveats?.length > 0 && (
          <ul className="mt-2.5 space-y-1.5">
            {island.caveats.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-400">
                <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-500" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 border-t border-white/5 pt-2 text-[10px] text-slate-500">
          Detection: USF/NOAA AFAI satellite · Drift forecast: NOAA currents + NASA wind
        </p>
      </div>

      {/* AI briefing */}
      <div className="glass flex-1 rounded-xl p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amberx" />
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-slate-200">
              AI Risk Briefing
            </h3>
          </div>
          <button
            onClick={() => startBriefing(island.id)}
            disabled={streaming}
            data-testid="regenerate-briefing-btn"
            className="rounded-md border border-cyan-500/30 p-1.5 text-cyan-300 transition-colors hover:bg-cyan-500/15 hover:text-cyan-200 disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${streaming ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-300" data-testid="briefing-text">
          {text}
          {streaming && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-cyan-400 align-middle" />}
          {error && !text && (
            <span className="text-amber-400">Briefing engine temporarily unavailable.</span>
          )}
          {!streaming && !text && !error && (
            <span className="text-slate-500">Awaiting synthesis…</span>
          )}
        </div>
      </div>

      {/* Sentinel-2 capture */}
      <div className="glass rounded-xl p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Satellite className="h-4 w-4 text-cyan-400" />
            <span className="font-display text-xs font-bold uppercase tracking-widest text-slate-200">
              Sentinel-2 Capture
            </span>
          </div>
          <button
            onClick={loadSentinel}
            disabled={satLoading}
            data-testid="load-sentinel-btn"
            className="rounded-md border border-cyan-500/30 px-2 py-1 font-mono-tel text-[10px] text-cyan-300 transition-colors hover:bg-cyan-500/15 hover:text-cyan-200 disabled:opacity-40"
          >
            {satLoading ? "SCANNING…" : "SCAN 10m"}
          </button>
        </div>
        {sat ? (
          <div className="overflow-hidden rounded-lg border border-cyan-500/20">
            {satLoading && (
              <div className="flex h-24 items-center justify-center text-[11px] text-slate-400">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Requesting L2A tile…
              </div>
            )}
            <img
              src={sat}
              alt="Sentinel-2 biomass scan"
              className={`w-full ${satLoading ? "hidden" : "block"}`}
              data-testid="sentinel-image"
              onLoad={() => setSatLoading(false)}
              onError={() => {
                setSatLoading(false);
                setSatError(true);
                setSat(null);
              }}
            />
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-cyan-500/20 text-center text-[11px] text-slate-500">
            {satError ? (
              <span className="text-amber-400">No cloud-free tile found — try again.</span>
            ) : (
              "Floating-Algae-Index highlight · 10 m resolution · green = biomass on water"
            )}
          </div>
        )}
      </div>
    </div>
  );
}
